/* eslint-disable import/no-unresolved */
import { useLocation } from '@reach/router';
import 'sal.js/dist/sal.css';
import AniLink from 'gatsby-plugin-transition-link/AniLink';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import sal from 'sal.js';

import ResImg from 'components/resImg';
import ResVideo from 'components/resVideo';

import IconArrow from '../icons/icon-arrow';

const CtaCard = ({
  data,
  slug,
  aspectRatioClass,
  containerClass,
  isAnimated,
  hasTag,
  figCaptionClass,
  mediaWrapperClass,
  maxWidth,
  selectedCategory,
  isPortrait,
}) => {
  useEffect(() => {
    if (isAnimated) {
      sal({ threshold: 0.25 });
    }
  }, [isAnimated]);

  const location = useLocation();
  const route = location.pathname.replace(/^\/|\/$/g, '');

  if (!data) {
    return null;
  }

  const { name, title, blurb } = data;
  const hasUrl = data.url !== undefined && data.url.url !== '' && data.url.url !== null;

  let link = slug || `${route}/${name?.toLowerCase().replace(/\s/g, '-')}`;

  // if link starts with en-nz remove en-nz

  if (link.startsWith('en-nz')) {
    link = link.replace('en-nz/', ''); // Strip 'en-nz/' if present
  }

  const hasVideo =
    data.card_video?.filename !== undefined &&
    data.card_video?.filename !== '' &&
    data.card_video?.filename !== null;

  let cardImageData;
  const hasPortraitImg =
    data?.portrait_image !== undefined &&
    data.portrait_image?.filename !== '' &&
    data.portrait_image?.filename !== null;
  const hasLandscapeImg =
    data?.card_image !== undefined &&
    data.card_image?.filename !== '' &&
    data.card_image?.filename !== null;
  const hasInteriorPortrait =
    data?.portrait_img_interior !== undefined &&
    data.portrait_img_interior?.filename !== '' &&
    data.portrait_img_interior?.filename !== null;
  const hasInteriorLandscape =
    data?.landscape_img_interior !== undefined &&
    data.landscape_img_interior?.filename !== '' &&
    data.landscape_img_interior?.filename !== null;
  const hasExteriorPortrait =
    data?.portrait_img_exterior !== undefined &&
    data.portrait_img_exterior?.filename !== '' &&
    data.portrait_img_exterior?.filename !== null;
  const hasExteriorLandscape =
    data?.landscape_img_exterior !== undefined &&
    data.landscape_img_exterior?.filename !== '' &&
    data.landscape_img_exterior?.filename !== null;

  const cardImageConditionals = {
    isPortraitInterior: isPortrait && selectedCategory === 'interiors' && hasInteriorPortrait,
    isPortraitExterior: isPortrait && selectedCategory === 'architecture' && hasExteriorPortrait,
    isDefaultPortrait: isPortrait && hasPortraitImg,
    isLandscapeInterior: !isPortrait && selectedCategory === 'interiors' && hasInteriorLandscape,
    isLandscapeExterior: !isPortrait && selectedCategory === 'architecture' && hasExteriorLandscape,
    isDefaultLandscape:
      !isPortrait && hasLandscapeImg && (!hasInteriorLandscape || !hasExteriorLandscape),
  };

  if (cardImageConditionals.isPortraitInterior) {
    cardImageData = (
      <ResImg
        mobile={data.portrait_img_interior}
        desktop={data.portrait_img_interior}
        className={aspectRatioClass}
        maxWidth={maxWidth}
        critical
      />
    );
  } else if (cardImageConditionals.isPortraitExterior) {
    cardImageData = (
      <ResImg
        mobile={data.portrait_img_exterior}
        desktop={data.portrait_img_exterior}
        className={aspectRatioClass}
        maxWidth={maxWidth}
        critical
      />
    );
  } else if (cardImageConditionals.isDefaultPortrait) {
    cardImageData = (
      <ResImg
        mobile={data.portrait_image}
        desktop={data.portrait_image}
        className={aspectRatioClass}
        maxWidth={maxWidth}
        critical
      />
    );
  } else if (cardImageConditionals.isLandscapeInterior) {
    cardImageData = (
      <ResImg
        mobile={data.landscape_img_interior}
        desktop={data.landscape_img_interior}
        className={aspectRatioClass}
        maxWidth={maxWidth}
        critical
      />
    );
  } else if (cardImageConditionals.isLandscapeExterior) {
    cardImageData = (
      <ResImg
        mobile={data.landscape_img_exterior}
        desktop={data.landscape_img_exterior}
        className={aspectRatioClass}
        maxWidth={maxWidth}
        critical
      />
    );
  } else if (cardImageConditionals.isDefaultLandscape) {
    cardImageData = (
      <ResImg
        mobile={data.card_image}
        desktop={data.card_image}
        className={aspectRatioClass}
        maxWidth={maxWidth}
        critical
      />
    );
  } else {
    cardImageData = (
      <ResImg
        mobile={data.card_image}
        desktop={data.card_image}
        className={aspectRatioClass}
        maxWidth={maxWidth}
        critical
      />
    );
  }

  return (
    <figure
      data-portrait={isPortrait}
      className={`relative flex h-full flex-col ${containerClass}`}
      {...(isAnimated
        ? {
            'data-sal': 'slide-up',
            'data-sal-duration': '1000',
            'data-sal-delay': '0',
            'data-sal-easing': 'ease',
          }
        : {})}
    >
      <div className={`flex-1 overflow-hidden bg-neutral-700 object-cover ${mediaWrapperClass}`}>
        {hasVideo ? (
          <ResVideo
            mobileVideo={data.card_video}
            desktopVideo={data.card_video}
            mobilePoster={data.card_image}
            desktopPoster={data.card_image}
            className={`${aspectRatioClass} h-full`}
          />
        ) : (
          cardImageData
        )}
      </div>
      <figcaption className={`space-y-4 pt-4 xl:space-y-8 xl:pt-8 ${figCaptionClass}`}>
        <div className="flex items-center justify-between">
          <div className="flex gap-6 text-xs tracking-wider lg:text-base xl:text-[1.375rem]">
            <h4 className="font-sans font-semibold">{name || title}</h4>
            {hasTag && data.title && <span>{data.title}</span>}
          </div>
          {hasUrl ? (
            <a
              href={`${data.url.url || data.url.cached_url}`}
              className='group flex items-center after:absolute after:inset-0 after:content-[""]'
              rel="noreferrer"
              {...(data.url.target ? { target: data.url.target, rel: 'noreferrer' } : {})}
            >
              <span className="sr-only">{name || title}</span>
              <span className="w-4 translate-x-0 transition-all duration-200 group-hover:translate-x-[0.15rem] lg:w-auto">
                <IconArrow className="w-full lg:w-auto" />
              </span>
            </a>
          ) : (
            <AniLink
              to={`/${link}`}
              className='group flex items-center after:absolute after:inset-0 after:content-[""]'
              direction="up"
              bg="#000000"
              duration={1}
              cover
            >
              <span className="sr-only">{name || title}</span>
              <span className="w-4 translate-x-0 transition-all duration-200 group-hover:translate-x-[0.15rem] lg:w-auto">
                <IconArrow className="w-full lg:w-auto" />
              </span>
            </AniLink>
          )}
        </div>
        {blurb && <div className="text-sm lg:text-lg lg:!leading-[1.12] xl:text-2xl">{blurb}</div>}
      </figcaption>
    </figure>
  );
};

CtaCard.propTypes = {
  data: PropTypes.object.isRequired,
  aspectRatioClass: PropTypes.string,
  containerClass: PropTypes.string,
  figCaptionClass: PropTypes.string,
  mediaWrapperClass: PropTypes.string,
  slug: PropTypes.string.isRequired,
  isAnimated: PropTypes.bool,
  hasTag: PropTypes.bool,
  maxWidth: PropTypes.number,
  selectedCategory: PropTypes.string,
  isPortrait: PropTypes.bool,
};

CtaCard.defaultProps = {
  aspectRatioClass: '',
  containerClass: '',
  figCaptionClass: '',
  mediaWrapperClass: '',
  isAnimated: false,
  hasTag: false,
  maxWidth: undefined,
  selectedCategory: '',
  isPortrait: false,
};
export default CtaCard;
